import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';

type ReturnedType = any;

type deleteThunkArg = {
  id: string;
  signal: any;
};

interface LatAndLongDetails {
  lat: number;
  lng: number;
}

interface GeoZone {
  id: number;
  geoZoneId?: string;
  geoZoneType: string;
  userID: string;
  latAndLongDetails: LatAndLongDetails[];
  arrivalStatusCode: number;
  departureStatusCode: number;
  shapeColor: string;
  radius: number;
  address: {
    place: string;
    neighbourhood: string;
    suburb: string;
    road: string;
    city: string;
    state: string;
    country: string;
    postcode: string;
  };
  location: string;
  stoppageLimit?: null | number;
  speedLimit?: null | number;
  landMark: null | string;
  isActive: number;
  geoZoneName: string;
  vehicleNumbers?: string[];
  createdAt: number;
  modifiedAt: number;
  createdBy: number;
  modifiedBy: number;
  notificationStatus: number;
}

interface GeofenceData {
  id: number;
  vehicleNumber?: string;
  geofenceRadius: number;
  location: string;
}

export type Location = {
  lat: number;
  lng: number;
};

type geofenceMapping = {
  vehicle: string[];
  geoZoneId?: string;
  assignVehicle?: any;
};

type NewGeofence = {
  vehicleNumber?: string;
  geozoneName?: string;
  geozoneID?: string;
  geoType: string;
  latAndLong: Location[];
  radius?: number | null;
  shapeColor: string;
  groupID?: string;
  speedLimit?: number;
  stoppageLimit?: number;
};

type addReturnedType = {
  data: NewGeofence;
};

interface InitialState {
  isLoading: boolean;
  type:
    | 'geofences'
    | 'save-geofence'
    | 'update-geofence'
    | 'active-geofence'
    | 'geofence-mapping'
    | 'delete-geofence';
  data: GeofenceData[] | any;
  vehicleList: string[] | [];
  error: string | null;
  count: any;
}

const initialState: InitialState = {
  isLoading: false,
  type: 'geofences',
  data: null,
  vehicleList: [],
  error: null,
  count: null
};

interface UpdatedGeofences {
  id: number;
  lat: number;
  lng: number;
  isActive: number;
  address: {
    place: string;
    neighbourhood: string;
    suburb: string;
    road: string;
    city: string;
    state: string;
    country?: string;
    postcode?: string;
  };
  vehicleNumber: string;
  location: string;
  geofenceRadius: number;
}

interface updateReturnedType {
  data: addReturnedType;
}

// Action function to get geofence details:
export const getGeofencesAction = createAsyncThunk<any, any>(
  'user/geofences',
  async (signal, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.GET_GEOFENCES}`
      });
      return response.data.data;
    } catch (error: any) {
      if (error?.message !== 'canceled')
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: error?.response?.data?.message,
            severity: 'error'
          })
        );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Action function to get geofence mapping:
export const getGeofenceMappingAction = createAsyncThunk<any, any>(
  'user/geofencemapping',
  async (data, thunkApi) => {
    const { pageNo = 1, pageSize = 5, geoZoneId } = data;
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.GEOFENCE_MAPPING_LIST}geozoneId=${geoZoneId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        signal: data?.signal
      });
      return response?.data?.data;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: error?.response?.data?.message || 'No Data Found',
            severity: 'error'
          })
        );
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Action function to get vehicle list details:
export const getVehicleListAction = createAsyncThunk(
  'user/geofences/vehicleList',
  async (_, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.GET_VEHICLE_LIST}`
      });
      return response.data.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Action function to add geofence details:
export const addGeofenceAction = createAsyncThunk<addReturnedType, NewGeofence>(
  'user/geofences/add',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: `${URLs.ADD_GEOFENCE}`,
        data
      });

      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response.data.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Action function to update mapping details:
export const updateGeofenceAction = createAsyncThunk<updateReturnedType, any>(
  'user/geofences/update',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'PUT',
        url: `${URLs.UPDATE_GEOFENCE}geozoneid=${data.geoZoneID}`,
        data: data?.payload,
        signal: data?.signal
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: 'Geofence updated successfully',
          severity: 'success'
        })
      );
      return response;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: error?.response?.data?.message,
            severity: 'error'
          })
        );
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Action function to deactivating/activating geofence details:
export const deactivateGeofence = createAsyncThunk<ReturnedType, deleteThunkArg>(
  'geofences/deactivate/activate',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'PATCH',
        url: `${URLs.DEACTIVATE_GEOFENCE}geozoneid=${data}`,
        signal: data?.signal
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Action function to delete geofence:
export const deleteGeofence = createAsyncThunk<ReturnedType, deleteThunkArg>(
  'geofence/delete',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'DELETE',
        url: `${URLs.DELETE_GEOFENCE}${data}`
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: 'Geofence deleted successfully',
          severity: 'success'
        })
      );
      return response;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

//action function for add geofence mapping

export const addGeofenceMapping = createAsyncThunk<any, geofenceMapping>(
  'add/geofencemapping',
  async (data, thunkApi) => {
    try {
      const { vehicle, geoZoneId, assignVehicle } = data;
      const response = await axiosInstance({
        method: 'PUT',
        url: `${URLs.ADD_GEOFENCE_MAPPING}geozoneid=${geoZoneId}&vehicleNumbers=${vehicle}`
      });
      if (response?.data?.message) {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: 'Geofence Mapping updated successfully',
            severity: 'success'
          })
        );
        assignVehicle(false);
      }

      return response;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

const geofenceSlice = createSlice({
  name: 'geofence',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      // get geofence
      .addCase(getGeofencesAction.pending, state => {
        state.isLoading = true;
        state.type = 'geofences';
        state.data = null;
        state.error = null;
      })
      .addCase(
        getGeofencesAction.fulfilled,
        (state, action: PayloadAction<GeoZone[]>) => {
          let updatedGeofences: UpdatedGeofences[] | [];
          let geofences: any = action.payload;
          if (geofences) {
            updatedGeofences = geofences.map((geofence: GeoZone) => ({
              id: geofence?.id,
              geoZoneId: geofence?.geoZoneId,
              geoType: geofence?.geoZoneType,
              latAndLongDetails: geofence?.latAndLongDetails,
              isActive: geofence?.isActive,
              location: geofence?.location,
              vehicleNumber: geofence?.vehicleNumbers,
              geofenceName: geofence?.geoZoneName,
              radius: geofence?.radius,
              shapeColor: geofence?.shapeColor
            }));
          } else updatedGeofences = [];
          state.data = updatedGeofences;
          state.isLoading = false;
          state.error = null;
        }
      )
      .addCase(getGeofencesAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload.response?.data?.error;
      })

      // get vehicle list
      .addCase(getVehicleListAction.pending, state => {
        state.isLoading = true;
        state.type = 'geofences';
        state.vehicleList = [];
        state.error = null;
      })
      .addCase(getVehicleListAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.vehicleList = action.payload;
      })
      .addCase(getVehicleListAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.vehicleList = [];
        state.error = action.payload.response?.data?.error;
      })

      // add geofence
      .addCase(addGeofenceAction.pending, state => {
        state.isLoading = true;
        state.type = 'save-geofence';
      })
      .addCase(
        addGeofenceAction.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.data = action.payload.data;
          state.isLoading = false;
          state.error = null;
        }
      )
      .addCase(addGeofenceAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload.response?.data?.error;
      })

      // update geofence
      .addCase(updateGeofenceAction.pending, state => {
        state.isLoading = true;
        state.type = 'update-geofence';
      })
      .addCase(
        updateGeofenceAction.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.data = action.payload.data;
          state.isLoading = false;
          state.error = null;
        }
      )
      .addCase(updateGeofenceAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload.response?.data?.error;
      })

      // deactivate geofence
      .addCase(deactivateGeofence.pending, state => {
        state.isLoading = true;
        state.type = 'active-geofence';
        state.error = null;
      })
      .addCase(
        deactivateGeofence.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload.data;
          state.error = null;
        }
      )
      .addCase(deactivateGeofence.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload.response?.data?.error;
      })

      // delete geofence
      .addCase(deleteGeofence.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.type = 'delete-geofence';
      })
      .addCase(deleteGeofence.fulfilled, (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.error = null;
      })
      .addCase(deleteGeofence.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload.response?.data?.error;
      })
      .addCase(addGeofenceMapping.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.type = 'geofence-mapping';
      })
      .addCase(addGeofenceMapping.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action?.payload?.data;
      })
      .addCase(addGeofenceMapping.rejected, (state, action) => {
        state.isLoading = false;
      });
  }
});

const geofenceMappingSlice = createSlice({
  name: 'geofencemapping',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      // get geofence mapping
      .addCase(getGeofenceMappingAction.pending, state => {
        state.isLoading = true;
        state.type = 'geofences';
        state.data = null;
        state.error = null;
      })
      .addCase(getGeofenceMappingAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        if (action?.payload) {
          const { count, geofenceDataList } = action?.payload;
          state.data = geofenceDataList;
          state.count = count;
        } else {
          state.count = 0;
        }
      })
      .addCase(getGeofenceMappingAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action?.payload?.response?.data?.error;
        state.count = 0;
      });
  }
});

export default geofenceSlice.reducer;
export const geofencemappingSlices = geofenceMappingSlice.reducer;
